import { useEffect, useState } from "react";
import { toast } from "sonner";
import { day } from "@/lib/money";
import ReplyBox from "@/components/suggestions/ReplyBox";
import Attachment from "@/components/community/Attachment";

const STATUSES = [["new", "New"], ["reviewed", "Reviewed"], ["done", "Done"]];
const TONE = { new: "border-[#FFD600] text-[#FFD600]", reviewed: "border-[#60A5FA] text-[#60A5FA]", done: "border-[#4ADE80] text-[#4ADE80]" };

export default function SuggestionsInbox({ api }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/admin/suggestions").then(({ data }) => setRows(data.suggestions)).catch(() => setRows("error"));
  }, [api]);
  if (rows === "error") return <p className="text-sm text-[#F87171]" data-testid="suggestions-error">Could not load suggestions. Refresh to try again.</p>;
  if (!rows) return <div className="font-mono-label text-xs text-muted-foreground">LOADING…</div>;

  const setStatus = async (id, status) => {
    const prev = rows;
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.put(`/admin/suggestions/${id}`, { status });
    } catch {
      setRows(prev);
      toast.error("Could not update. Try again.");
    }
  };
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <section className="brutal-border" data-testid="suggestions-inbox">
      <div className="p-4 sm:p-5 border-b border-[#262626] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="font-mono-label text-[10px] text-muted-foreground">APP SUGGESTIONS · PRIVATE · {rows.length} TOTAL</div>
        <div className="flex flex-wrap">
          {[["all", `All ${rows.length}`], ...STATUSES.map(([k, l]) => [k, `${l} ${count(k)}`])].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k} data-testid={`suggestions-filter-${k}`}
              className={`min-h-[36px] px-3 font-mono-label text-[10px] border -ml-px first:ml-0 transition-colors ${filter === k ? "border-[#FFD600] text-[#FFD600] relative z-10" : "border-[#262626] text-muted-foreground hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="suggestions-empty">No suggestions here yet.</p>}
      <div className="divide-y divide-[#262626]">
        {shown.map((s) => (
          <article key={s.id} className="p-4 sm:p-5 flex flex-col md:flex-row gap-3 md:gap-6" data-testid={`suggestion-row-${s.id}`}>
            <div className="md:w-60 shrink-0 min-w-0">
              <div className="font-semibold truncate" data-testid={`suggestion-name-${s.id}`}>{s.name || s.email || "Unnamed client"}</div>
              <div className="text-xs text-muted-foreground truncate">{[s.business, s.email].filter(Boolean).join(" · ")}</div>
              <div className="text-[11px] text-muted-foreground mt-1" data-testid={`suggestion-date-${s.id}`}>{day(s.created_at)} · {new Date(s.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm whitespace-pre-wrap break-words" data-testid={`suggestion-message-${s.id}`}>{s.message}</p>
              {s.attachment && <Attachment api={api} url={`/suggestions/${s.id}/file`} att={s.attachment} testid={`suggestion-att-${s.id}`} />}
              <ReplyBox api={api} s={s} onSaved={(id, patch) => setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)))} />
            </div>
            <div className="flex md:flex-col gap-1.5 shrink-0">
              {STATUSES.map(([k, l]) => (
                <button key={k} onClick={() => setStatus(s.id, k)} aria-pressed={s.status === k} data-testid={`suggestion-status-${k}-${s.id}`}
                  className={`min-h-[32px] px-3 border font-mono-label text-[10px] transition-colors ${s.status === k ? TONE[k] : "border-[#262626] text-muted-foreground hover:text-white"}`}>{l}</button>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
